/**
 * Responsibility: the plot at the bottom of a card dialog, and the choice of
 * what is plotted in it.
 *
 * Shared by both dialogs so the bottom of each looks and behaves the same.
 * Two series are worth charting and they are on different axes, so they get
 * a mini-tab each rather than a shared pair of scales.
 *
 * **Sale history** is what the card has actually sold for, with the average
 * drawn as a dashed line across it. The average matters more than it looks:
 * a run of sales scattered either side of it is a real price, and the same
 * average from one sale at double and one at half is not - only the picture
 * separates them.
 *
 * **Population** is 52 weekly points from BetterPop, and only Pokemon cards
 * from the discovery board have it. It answers the question the payoff
 * numbers cannot - whether the scarcity being priced is holding or being
 * eroded while the operator thinks about it. Grading takes months, so a
 * population climbing steeply is a reason not to buy however good the
 * multiple looks.
 *
 * A tab appears only when its series exists, and the area opens on whichever
 * one does. A sports card simply has no population tab, rather than a tab
 * that explains its own emptiness.
 */
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { LineSeriesChart } from '@kit/components/charts/LineSeriesChart';
import type { SoldSale } from '../../api/client';
import { GradePicker } from './GradePicker';
import { SalesChart } from './SalesHistory';
import { bucketsOf, defaultBucket, salesIn } from './saleGrades';

const CHART_HEIGHT = 148;

type CardChartAreaProps = {
  sales: SoldSale[];
  /** Total graded per week, oldest first. Pokemon discovery cards only. */
  population?: number[];
  populationAdded?: number | null;
  loading?: boolean;
  /**
   * The grade this page is already about - the grade a holding is in, or
   * the grade a headline price came from. Opens the plot on it, because
   * that is the question already being asked.
   */
  preferredGrade?: string | null;
};

/** Weekly points, labelled by how long ago they are. */
function populationRows(series: number[]) {
  const last = series.length - 1;
  return series.map((total, index) => ({
    x: index === last ? 'now' : `${last - index}w`,
    total,
  }));
}

function Empty({ loading }: { loading: boolean }) {
  return (
    <Box
      sx={{
        height: CHART_HEIGHT,
        borderRadius: 1,
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'action.hover',
        px: 2,
      }}
    >
      <Stack spacing={0.5} alignItems="center" sx={{ textAlign: 'center' }}>
        <ShowChartIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Looking for sale history' : 'Nothing to plot for this card'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 430 }}>
          No catalogue product matched this card confidently, so there are no completed sales
          to draw. The matcher would rather find nothing than attach another card&apos;s
          history to this one.
        </Typography>
      </Stack>
    </Box>
  );
}

export function CardChartArea({
  sales,
  population = [],
  populationAdded = null,
  loading = false,
  preferredGrade = null,
}: CardChartAreaProps) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [grade, setGrade] = useState<string | null>(null);

  const buckets = useMemo(() => bucketsOf(sales), [sales]);
  const bucket = grade ?? defaultBucket(buckets, preferredGrade);
  const shown = useMemo(() => salesIn(sales, bucket), [sales, bucket]);

  const available: string[] = [];
  if (sales.length > 0) available.push('sales');
  if (population.length > 0) available.push('population');

  if (available.length === 0) return <Empty loading={loading} />;

  // An explicit choice wins; otherwise open on whichever series exists.
  const view = chosen && available.includes(chosen) ? chosen : available[0];

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Tabs
        value={view}
        onChange={(_event, next: string) => setChosen(next)}
        variant="scrollable"
        scrollButtons={false}
      >
        {available.includes('sales') ? (
          <Tab value="sales" label={`Sale history (${sales.length})`} />
        ) : null}
        {available.includes('population') ? (
          <Tab
            value="population"
            label={`Population${
              populationAdded !== null ? ` (+${populationAdded.toLocaleString()})` : ''
            }`}
          />
        ) : null}
      </Tabs>

      {view === 'sales' ? (
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <GradePicker buckets={buckets} value={bucket} onChange={setGrade} />
          <SalesChart sales={shown} loading={loading} />
        </Stack>
      ) : null}

      {view === 'population' ? (
        <LineSeriesChart
          rows={populationRows(population)}
          xKey="x"
          height={CHART_HEIGHT}
          loading={loading}
          series={[{ key: 'total', label: 'Total graded' }]}
          emptyMessage="No population history for this card."
        />
      ) : null}
    </Stack>
  );
}
